import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Spinner } from 'tamagui'
import { CobuButton, CobuStack, CobuText, CobuHeader, CobuBackButton, CobuAlert } from '@cobu/ui'
import { useChannelList } from '../../hooks/useChannelList'
import { getMyBlockedUsers } from '@cobu/api-client/blocked-users'
import { useQueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import { banUserOnDM } from '../../hooks/userHooks'
import { showSuccessMessage, showErrorMessage } from '../../utils/messageToast'
import { Strings } from '../../constants/Strings';
import { ReportUserModal } from '../../components/ui/ReportUserModal'
import { useResidentRestrictions } from '../../hooks/useResidentRestrictions'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import { logger } from '../../utils/logger'

export default function ChatChannel() {
  const { channelId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useSelector((state: RootState) => state.auth.user)
  const { canReport } = useResidentRestrictions()
  const { chatClient, isLoading, error, isStreamAvailable, Chat, Channel, MessageList, MessageInput, i18nInstance } = useChannelList({ enabled: true })
  const [channel, setChannel] = useState<any | null>(null)
  const [headerTitle, setHeaderTitle] = useState<string>('Conversation')
  const [otherUserId, setOtherUserId] = useState<string | null>(null)
  const [isMoreOpen, setIsMoreOpen] = useState(false)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const [isBanned, setIsBanned] = useState<boolean>(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportSuccessAlertOpen, setReportSuccessAlertOpen] = useState(false)

  // Fetch channel by id when client ready
  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!chatClient || !channelId) return
      try {
        const raw = String(channelId)
        const isCid = raw.includes(':')
        const [type, id] = isCid ? raw.split(':') : ['messaging', raw]
        const ch = chatClient.channel(type, id)
        await ch.watch()
        if (!mounted) return
        setChannel(ch)
        // Derive header title from other member
        try {
          const members = (await ch.queryMembers({})).members || []
          const me = chatClient._user?.id
          const other = members.find((m: any) => m?.user?.id !== me)
          if (other?.user?.name) setHeaderTitle(other.user.name)
          if (other?.user?.id) setOtherUserId(other.user.id)
        } catch {}
        // Mute state (best effort)
        try {
          const muted = !!ch.muteStatus?.().muted
          setIsMuted(muted)
        } catch {}
        // Banned state - check both Stream Chat bans and our blocked users list
        try {
          // Check Stream Chat bans
          const bans = await chatClient.queryBannedUsers({ channel_cid: ch.cid }, { created_at: -1 })
          const me = chatClient._user?.id
          const other = (await ch.queryMembers({})).members.find((m: any) => m?.user?.id !== me)
          const streamBanned = bans?.bans?.some((b: any) => b?.user?.id === other?.user?.id)
          
          // Also check our blocked users list
          if (other?.user?.id) {
            const blockedUsers = await getMyBlockedUsers()
            const isBlocked = blockedUsers.includes(other.user.id)
            setIsBanned(!!streamBanned || isBlocked)
          } else {
            setIsBanned(!!streamBanned)
          }
        } catch {}
      } catch (e) {
        setChannel(null)
      }
    })()
    return () => { mounted = false }
  }, [chatClient, channelId])

  const handleToggleMore = useCallback(() => setIsMoreOpen((s) => !s), [])

  const handleReportUser = useCallback(() => {
    setShowReportModal(true)
    setIsMoreOpen(false)
  }, [])

  const handleCloseReportModal = useCallback(() => {
    setShowReportModal(false)
  }, [])

  const handleReportSuccess = useCallback(() => {
    setShowReportModal(false)
    setReportSuccessAlertOpen(true)
  }, [])

  const handleToggleMute = useCallback(async () => {
    if (!channel) return
    const strings = Strings.get()
    try {
      if (isMuted) {
        await channel.unmute()
        setIsMuted(false)
        showSuccessMessage(strings.chat.channel.alerts.channelUnmuted)
      } else {
        await channel.mute()
        setIsMuted(true)
        showSuccessMessage(strings.chat.channel.alerts.channelMuted)
      }
    } catch (e) {
      logger.error('Error toggling mute:', e)
      showErrorMessage('Error', 'Failed to update mute status')
    }
    setIsMoreOpen(false)
  }, [channel, isMuted])

  const handleBlockUser = useCallback(async () => {
    if (!otherUserId || !user?.uuid || !channel) return
    
    const strings = Strings.get()
    // Get channel ID - prefer id, fallback to extracting from cid
    let channelId = channel.id || ''
    if (!channelId && channel.cid) {
      // Extract ID from CID format "messaging:uuid"
      const parts = channel.cid.split(':')
      channelId = parts.length > 1 ? parts[1] : channel.cid
    }
    
    if (!channelId) {
      showErrorMessage('Error', 'Unable to identify channel')
      setIsMoreOpen(false)
      return
    }
    
    try {
      const unban = isBanned
      const result = await banUserOnDM({
        channelId,
        blockingUserId: user.uuid,
        blockedUserId: otherUserId,
        unban,
      })
      
      // Treat as success if result.success is true OR if result has data property (200 response)
      // API returns {data: {duration: "..."}} on success, so check for data property
      const isSuccess = result.success !== false && (result.success === true || (result as any).data !== undefined)
      
      if (isSuccess) {
        setIsBanned(!unban)
        // Show success message using chat strings
        if (unban) {
          showSuccessMessage(strings.chat.actions.unblock)
        } else {
          showSuccessMessage(strings.chat.actions.block)
        }
        // Invalidate queries to refresh UI
        await queryClient.invalidateQueries({ queryKey: ['blocked-users'] })
      } else {
        // Show error message from response or default
        const errorMsg = result.message || (unban ? 'Failed to unblock user' : 'Failed to block user')
        showErrorMessage('Error', errorMsg)
      }
    } catch (e: any) {
      logger.error('Error blocking/unblocking user:', e)
      const errorMessage = e?.response?.data?.message || e?.message || 'An error occurred'
      showErrorMessage('Error', errorMessage)
    }
    setIsMoreOpen(false)
  }, [otherUserId, user?.uuid, isBanned, channel, queryClient])

  const giphyPrefixRef = useRef<string | null>(null)

  const handleGiphyShortcut = useCallback(() => {
    try {
      const prefix = '/giphy '
      giphyPrefixRef.current = prefix
      
      // Find the input element (could be textarea or contenteditable div)
      const findInput = (): HTMLElement | null => {
        return document.querySelector(
          '.str-chat__textarea__textarea, textarea[data-testid="message-input"], .cs-message-input__content-editor, [contenteditable="true"]'
        ) as HTMLElement | null
      }
      
      const input = findInput()
      if (!input) {
        // Retry after a short delay if input not found
        setTimeout(() => {
          const retryInput = findInput()
          if (retryInput) {
            applyGiphyPrefix(retryInput)
          }
        }, 100)
        return
      }
      
      applyGiphyPrefix(input)
      
      // Set up a more targeted approach: watch for focus events and re-apply if value was cleared
      let lastKnownValue = getInputValue(input)
      let reapplyAttempts = 0
      const maxReapplyAttempts = 10
      
      const checkAndReapply = () => {
        const currentInput = findInput()
        if (!currentInput || !giphyPrefixRef.current) return
        
        const currentValue = getInputValue(currentInput)
        // Only re-apply if value was cleared or lost the prefix, and we haven't exceeded attempts
        if (reapplyAttempts < maxReapplyAttempts && 
            currentValue !== lastKnownValue && 
            !currentValue.startsWith(giphyPrefixRef.current)) {
          // Value was cleared or changed, re-apply prefix
          const newValue = giphyPrefixRef.current + currentValue.replace(/^\/giphy\s*/i, '')
          setInputValue(currentInput, newValue)
          lastKnownValue = newValue
          reapplyAttempts++
        } else if (currentValue.startsWith(giphyPrefixRef.current)) {
          lastKnownValue = currentValue
        }
      }
      
      // Watch for focus events
      const handleFocus = () => {
        setTimeout(checkAndReapply, 10)
        setTimeout(checkAndReapply, 50)
        setTimeout(checkAndReapply, 100)
      }
      
      input.addEventListener('focus', handleFocus)
      
      // Also watch for input changes that might clear the value
      const handleInputChange = () => {
        const currentInput = findInput()
        if (currentInput) {
          const value = getInputValue(currentInput)
          if (value && !value.startsWith(prefix)) {
            // User removed the prefix, stop enforcing it
            cleanup()
            currentInput.removeEventListener('input', handleInputChange)
            currentInput.removeEventListener('focus', handleFocus)
          } else {
            lastKnownValue = value
          }
        }
      }
      input.addEventListener('input', handleInputChange, { once: false })
      
      // Clean up after 3 seconds or when user removes prefix
      const cleanup = () => {
        giphyPrefixRef.current = null
        const currentInput = findInput()
        if (currentInput) {
          currentInput.removeEventListener('focus', handleFocus)
          currentInput.removeEventListener('input', handleInputChange)
        }
      }
      setTimeout(cleanup, 3000)
      
      // Focus the input
      setTimeout(() => {
        const currentInput = findInput()
        if (currentInput) {
          currentInput.focus()
          // Set cursor to end
          if (currentInput instanceof HTMLTextAreaElement || currentInput instanceof HTMLInputElement) {
            const value = getInputValue(currentInput)
            currentInput.setSelectionRange(value.length, value.length)
          } else {
            // For contenteditable, move cursor to end
            const range = document.createRange()
            const selection = window.getSelection()
            range.selectNodeContents(currentInput)
            range.collapse(false)
            selection?.removeAllRanges()
            selection?.addRange(range)
          }
        }
      }, 50)
    } catch {
      // best-effort helper; ignore errors
    }
  }, [])
  
  const getInputValue = (input: HTMLElement): string => {
    if (input instanceof HTMLTextAreaElement || input instanceof HTMLInputElement) {
      return input.value || ''
    }
    return input.textContent || input.innerText || ''
  }
  
  const setInputValue = (input: HTMLElement, value: string) => {
    if (input instanceof HTMLTextAreaElement || input instanceof HTMLInputElement) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement?.prototype || window.HTMLInputElement?.prototype,
        'value'
      )?.set
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(input, value)
      } else {
        input.value = value
      }
      // Dispatch input event
      const event = new Event('input', { bubbles: true, cancelable: true })
      input.dispatchEvent(event)
    } else {
      // Contenteditable div
      input.textContent = value
      input.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }))
    }
  }
  
  const applyGiphyPrefix = (input: HTMLElement) => {
    const prefix = '/giphy '
    const currentValue = getInputValue(input)
    const newValue = currentValue.startsWith(prefix) 
      ? currentValue 
      : prefix + currentValue.replace(/^\/giphy\s*/i, '')
    
    setInputValue(input, newValue)
  }

  if (!isStreamAvailable) {
    return <CobuStack padding="$4"><CobuText>{Strings.get().chat.channel.webFallback.message}</CobuText></CobuStack>
  }

  if (isLoading || !Chat || !Channel || !MessageList || !MessageInput) {
    return (
      <CobuStack padding="$4" ai="center" jc="center">
        <Spinner size="large" />
      </CobuStack>
    )
  }

  if (error) return null

  if (!channel) {
    return (
      <CobuStack padding="$4" ai="center" jc="center">
        <CobuText>{Strings.get().chat.channel.loading.conversation}</CobuText>
      </CobuStack>
    )
  }

  return (
    <Chat client={chatClient} {...(i18nInstance ? { i18nInstance } : {})}>
      <CobuStack>
        <CobuHeader
          title={headerTitle}
          renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />}
          renderRight={() => (
            <CobuButton backgroundColor="transparent" size="$4" px="$1" onPress={handleToggleMore}>
              <MoreHorizIcon fontSize="medium" htmlColor="#272163" />
            </CobuButton>
          )}
        />

        {/* Simple more menu */}
        {isMoreOpen && (
          <CobuStack position="absolute" right={16} top={56} backgroundColor="$white" borderWidth={1} borderColor="$borderColor" borderRadius={8} zIndex={20} layout="vertical">
            <CobuButton backgroundColor="transparent" size="$4" onPress={handleToggleMute} style={{ display: 'block', padding: '8px 12px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer' }}>
<CobuText variant="body">{isMuted ? Strings.get().chat.channel.menu.unmuteConversation : Strings.get().chat.channel.menu.muteConversation}</CobuText>
            </CobuButton>
            {canReport && (
            <CobuButton backgroundColor="transparent" size="$4" onPress={handleReportUser} style={{ display: 'block', padding: '8px 12px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <CobuText variant="body">{Strings.get().chat.channel.reportUser.title}</CobuText>
            </CobuButton>
            )}
            <CobuButton backgroundColor="transparent" size="$4" color="$cobuRed" onPress={handleBlockUser} style={{ display: 'block', padding: '8px 12px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626' }}>
              <CobuText variant="body">{isBanned ? Strings.get().chat.channel.menu.unblockUser : Strings.get().chat.channel.menu.blockUser}</CobuText>
            </CobuButton>
          </CobuStack>
        )}

        <Channel channel={channel}>
          <CobuStack height="calc(100vh - 140px)" display="flex" flexDirection="column" position="relative">
            <CobuStack flex={1} minHeight={0}>
              <MessageList />
            </CobuStack>
            
            {/* Banned user overlay */}
            {isBanned && (
              <CobuStack
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                backgroundColor="rgba(0, 0, 0, 0.5)"
                zIndex={10}
                ai="center"
                jc="center"
                px="$4"
              >
                <CobuStack
                  backgroundColor="$white"
                  borderRadius={8}
                  padding="$4"
                  maxWidth="90%"
                >
                  <CobuText variant="body" color="$cobuDarkBlue" ta="center" fontWeight="semibold">
                    {Strings.get().chat.channel.bannedUser.warning}
                  </CobuText>
                </CobuStack>
              </CobuStack>
            )}
            
            {/* Bottom bar - hide when banned */}
            {!isBanned && (
              <CobuStack layout="horizontal" ai="flex-end">
                <CobuButton
                  backgroundColor="transparent"
                  size="$1"
                  pl="$3"
                  onPress={handleGiphyShortcut}
                  aria-label="Giphy"
                >
                  <FlashOnIcon fontSize="medium" htmlColor="gray" />
                </CobuButton>
                <CobuStack f={1} ml="$0">
                  <MessageInput focus />
                </CobuStack>
              </CobuStack>
            )}
          </CobuStack>
        </Channel>
        
        {/* Report User Modal */}
        {otherUserId && user?.uuid && (
          <ReportUserModal
            visible={showReportModal}
            onClose={handleCloseReportModal}
            reportedUserUuid={otherUserId}
            reportedByUuid={user.uuid}
            onSuccess={handleReportSuccess}
          />
        )}
        
        {/* Report Success Alert */}
        <CobuAlert
          open={reportSuccessAlertOpen}
          onOpenChange={setReportSuccessAlertOpen}
          title={Strings.get().ui.reportUser.userReported}
          description={Strings.get().ui.reportUser.userReportedMessage}
          onAccept={() => {
            setReportSuccessAlertOpen(false)
          }}
        />
      </CobuStack>
    </Chat>
  )
}


